import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { testId } from '../../app/testId';
import { cx } from '../cx';
import styles from './Skeleton.module.css';

// Skeleton — DESIGN_SYSTEM §12 row 28 (`.skeleton`) and REQ-287 AC1: loading is a skeleton that mirrors the
// real layout, never a spinner, never drawn over data that is already there. Shimmer 1.6 s, static under
// reduced motion. `SkeletonGroup` is the container a screen wraps its bones in: `aria-busy`, a name for the
// screen reader ("Loading campaigns"), and — after 8 s — "This is taking longer than usual" (not an error;
// the request itself gives up at 20 s and shows ErrorState). Card / rows / chart shapes are ready-made.

export type SkeletonShape = 'text' | 'title' | 'block' | 'circle' | 'pill';

export interface SkeletonProps {
  shape?: SkeletonShape;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
}

/** One bone. Decorative: the group carries the meaning. */
export function Skeleton({ shape = 'text', width, height, className, style }: SkeletonProps) {
  return (
    <span
      className={cx(styles.bone, styles[shape], className)}
      style={{ width, height, ...style }}
      aria-hidden="true"
    />
  );
}

export const SLOW_LOAD_MS = 8_000;
export const SLOW_LOAD_TEXT = 'This is taking longer than usual';

export interface SkeletonGroupProps {
  /** What is loading, for the screen reader: "Loading campaigns". */
  label: string;
  children: ReactNode;
  /** After this many ms the slow note appears; 0 turns it off. */
  slowAfterMs?: number;
  slowText?: string;
  /** `state.loading` on a screen (TEST_PLAN §1.4); the slow note is `<testID>.slow`. */
  testID?: string;
  className?: string;
}

export function SkeletonGroup({
  label,
  children,
  slowAfterMs = SLOW_LOAD_MS,
  slowText = SLOW_LOAD_TEXT,
  testID,
  className,
}: SkeletonGroupProps) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (slowAfterMs <= 0) return;
    const timer = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(timer);
  }, [slowAfterMs]);
  return (
    <div
      role="group"
      aria-label={label}
      aria-busy="true"
      className={cx(styles.group, className)}
      data-testid={testID}
    >
      {children}
      {slow ? (
        <p className={styles.slow} data-testid={testID ? testId(testID, 'slow') : undefined}>
          {slowText}
        </p>
      ) : null}
    </div>
  );
}

/** The bones of a KPI card: label, value, delta line, caption (components.html "Loading"). */
export function SkeletonCard({ hero = false, className }: { hero?: boolean; className?: string }) {
  return (
    <div className={cx(styles.card, className)} aria-hidden="true">
      <Skeleton width={72} />
      <Skeleton shape="title" width={hero ? 200 : 148} height={hero ? 48 : 30} />
      <Skeleton width={120} />
      <Skeleton width="90%" style={{ marginTop: 2 }} />
    </div>
  );
}

/** The bones of a metric tile: label, value beside a sparkline, delta. */
export function SkeletonTile({ className }: { className?: string }) {
  return (
    <div className={cx(styles.tile, className)} aria-hidden="true">
      <Skeleton width={64} height={8} />
      <div className={styles.tileRow}>
        <Skeleton shape="title" width={56} height={24} />
        <Skeleton shape="block" width={96} height={28} />
      </div>
      <Skeleton width={40} height={8} />
    </div>
  );
}

const ROW_WIDTHS = [190, 230, 160, 210, 175, 200];

/** List / table rows: a thumbnail, a name with a sub-line, and trailing figures. */
export function SkeletonRows({
  rows = 3,
  trailing = 3,
  thumbnail = true,
  className,
}: {
  rows?: number;
  /** How many right-aligned figures per row. */
  trailing?: number;
  thumbnail?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(styles.rows, className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={styles.row}>
          {thumbnail ? <Skeleton shape="circle" className={styles.thumb} /> : null}
          <div className={styles.lines}>
            <Skeleton width={ROW_WIDTHS[i % ROW_WIDTHS.length]} />
            <Skeleton width={96 - (i % 3) * 16} height={8} />
          </div>
          {Array.from({ length: trailing }, (_, j) => (
            <Skeleton key={j} width={52 + ((i + j) % 3) * 20} className={styles.trailing} />
          ))}
        </div>
      ))}
    </div>
  );
}

const BAR_HEIGHTS = [40, 55, 48, 70, 62, 80, 66, 58, 74, 52, 68, 90, 60, 72];

/** A chart's silhouette: a title line, columns of varying height on a baseline. */
export function SkeletonChart({
  height = 160,
  bars = 14,
  className,
}: {
  height?: number;
  bars?: number;
  className?: string;
}) {
  return (
    <div className={cx(styles.chart, className)} aria-hidden="true">
      <Skeleton width={120} />
      <div className={styles.bars} style={{ height }}>
        {Array.from({ length: bars }, (_, i) => (
          <Skeleton
            key={i}
            shape="block"
            className={styles.bar}
            height={`${BAR_HEIGHTS[i % BAR_HEIGHTS.length]}%`}
          />
        ))}
      </div>
      <Skeleton className={styles.axis} height={1} width="100%" />
    </div>
  );
}
